import {
  findTimeControl,
  timestamp,
  type Actor,
  type Opponent,
  type RoomId,
  type RoomState,
} from './types.js'
import type { Command, RoomError, RoomEvent } from './events.js'
import { createRoom, replay } from './room.js'

/**
 * Everything needed to reconstruct a room: its identity, its settings, and the
 * log.
 *
 * There is no serialised `RoomState` on the wire. State is a fold over events,
 * so shipping the events lets the client rebuild it with the same reducer the
 * server ran — which means the two can never disagree about what a position is,
 * and there is no bespoke encoder to keep in sync with the state shape.
 */
export interface RoomSnapshot {
  readonly roomId: string
  readonly timeControlId: string
  readonly createdAt: number
  /** Absent means a human opponent, so old snapshots still materialize. */
  readonly opponent?: Opponent
  readonly events: readonly RoomEvent[]
}

export type ServerMessage =
  | {
      readonly type: 'snapshot'
      readonly snapshot: RoomSnapshot
      readonly you: Actor
      /** The server's clock, so the client can correct for skew. */
      readonly serverTimeMs: number
      readonly present: readonly [boolean, boolean]
    }
  | { readonly type: 'events'; readonly events: readonly RoomEvent[] }
  | { readonly type: 'present'; readonly present: readonly [boolean, boolean] }
  | { readonly type: 'rejected'; readonly error: RoomError }

export type ClientMessage = { readonly type: 'command'; readonly command: Command }

export const materialize = (snapshot: RoomSnapshot): RoomState => {
  const preset = findTimeControl(snapshot.timeControlId)
  const room = createRoom(
    snapshot.roomId as RoomId,
    preset?.control ?? { kind: 'unlimited' },
    timestamp(snapshot.createdAt),
    snapshot.opponent ?? { kind: 'human' },
  )
  return replay(room, snapshot.events)
}
